/* eslint-disable no-underscore-dangle */
import axios from 'axios';

/**
 * Manage the access token
 * @param {AuthObject} auth
 * @returns {void}
 */
export const setAuthentication = (auth) => {
  // Store the token
  localStorage.setItem('auth', auth.accessToken);
};
/**
 * Manage the permissions map
 * @param {object} permissionsMap
 * @returns {void}
 */
export const setPermissions = (permissionsMap) => {
  // Store permissions, to avoid having permissions in plain sight we encode it in b64
  localStorage.setItem('prm', btoa(JSON.stringify(permissionsMap)));
};
/**
 * Return the string permissions array from the permission token array booleans
 * @param {{[key:string]:Array<string>}} permissionsMap
 * @returns {{[key:string]:Array<string>}}
 */
export const decodeTokenPermissions = (permissionsMap) => {
  // Decode the b64 token to get the permissions
  const localToken = localStorage.getItem('auth');
  const token = localToken.split('.')[1];
  const decodedToken = JSON.parse(atob(token));
  const tokenPermissionsMapString = decodedToken.prm;
  // Make the permissions mapping between the token and the permissionsMap
  const [permissionKey, permissionsString] = tokenPermissionsMapString.split(':');
  const permissionsList = permissionsMap[permissionKey];
  const userPermissions = permissionsList.filter((_, index) => permissionsString[index] === '1');

  return { [permissionKey]: userPermissions };
};
/**
 * Manage the access token
 * @returns {void}
 */
export const deleteAuthentication = () => {
  // Delete the token from localstorage
  localStorage.removeItem('auth');
  localStorage.removeItem('prm');
  // Redirect to login
  window.location.href = '/login';
};
/**
 * Refresh the access token
 * @param {import('axios').AxiosError & { config: { _retry?: boolean }}} axiosError
 * @returns {Promise<import('axios').AxiosResponse|import('axios').AxiosError>}
 */
export const refreshAccessToken = async (axiosError) => {
  const originalRequest = axiosError.config;

  if (axiosError.response && axiosError.response.status === 401 && !originalRequest._retry) {
    originalRequest._retry = true;

    try {
      const response = await axios({
        method: 'post',
        baseURL: import.meta.env.VITE_API_URL,
        url: '/identity-access/v1-0/pools/admin/token',
        withCredentials: true,
        data: {
          grantType: 'refresh_token',
        },
      });

      const newAccessToken = response.data.accessToken;

      setAuthentication({ accessToken: newAccessToken });
      originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
      return axios(originalRequest);
    } catch (refreshError) {
      deleteAuthentication();
      window.location.replace(`/login?redirect=${window.location.pathname}`);
    }
  }
  return Promise.reject(axiosError);
};
/**
 * Check if connected user has all the permissions passed as argument.
 * @param {Array<string>} userPermissions
 * @param {Array<string>} permissionsToCheck
 * @returns {boolean}
 */
export const hasPermission = (userPermissions, permissionsToCheck) => permissionsToCheck
  .every((permission) => userPermissions.includes(permission));
